import React, { useEffect, useState, useCallback } from "react";
import { api, eur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PRESETS = [
  ["today", "Oggi"], ["yesterday", "Ieri"], ["last7", "Ultimi 7 giorni"],
  ["last30", "Ultimi 30 giorni"], ["this_month", "Questo mese"],
  ["last_month", "Mese scorso"], ["custom", "Personalizzato"],
];
const STATIONS = [["", "Tutte"], ["umbrella", "Ombrelloni"], ["table", "Tavoli"], ["sunbed", "Lettini"], ["vip", "VIP"]];
const STATUSES = [["", "Tutti"], ["new", "Nuovo"], ["accepted", "Accettato"], ["in_preparation", "In preparazione"],
                  ["ready", "Pronto"], ["delivered", "Consegnato"], ["cancelled", "Annullato"], ["rejected", "Rifiutato"]];
const PAYMENTS = [["", "Tutti"], ["on_delivery", "Alla consegna"], ["station_tab", "Conto postazione"], ["at_cashier", "Alla cassa"]];

export default function AdminReports() {
  const [preset, setPreset] = useState("today");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [stationType, setStationType] = useState("");
  const [status, setStatus] = useState("");
  const [payment, setPayment] = useState("");
  const [operator, setOperator] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (preset !== "custom") params.set("preset", preset);
      else {
        if (fromDate) params.set("from_date", fromDate);
        if (toDate) params.set("to_date", toDate);
      }
      if (stationType) params.set("station_type", stationType);
      if (status) params.set("status", status);
      if (payment) params.set("payment_method", payment);
      if (operator) params.set("operator_email", operator);
      if (phone) params.set("customer_phone", phone);
      if (code) params.set("order_code", code);
      const { data } = await api.get(`/admin/reports/summary-filtered?${params}`);
      setSummary(data);
    } finally { setLoading(false); }
  }, [preset, fromDate, toDate, stationType, status, payment, operator, phone, code]);

  useEffect(() => { load(); }, [load]);

  const reset = () => {
    setPreset("today"); setFromDate(""); setToDate("");
    setStationType(""); setStatus(""); setPayment(""); setOperator(""); setPhone(""); setCode("");
  };

  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  return (
    <div>
      <div className="eyebrow">Analisi</div>
      <h1 className="text-4xl mt-1">Report</h1>

      <section className="card-elevated p-4 mt-5">
        <div className="grid gap-3 md:grid-cols-4">
          <div><Label>Periodo</Label>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger data-testid="rpt-preset"><SelectValue /></SelectTrigger>
              <SelectContent>{PRESETS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {preset === "custom" && (
            <>
              <div><Label>Dal</Label><Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="rpt-from" /></div>
              <div><Label>Al</Label><Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="rpt-to" /></div>
            </>
          )}
          <div><Label>Postazione</Label>
            <Select value={stationType || "all"} onValueChange={(v) => setStationType(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="rpt-station"><SelectValue /></SelectTrigger>
              <SelectContent>{STATIONS.map(([v, l]) => <SelectItem key={v || "all"} value={v || "all"}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Stato</Label>
            <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="rpt-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map(([v, l]) => <SelectItem key={v || "all"} value={v || "all"}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Pagamento</Label>
            <Select value={payment || "all"} onValueChange={(v) => setPayment(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="rpt-pay"><SelectValue /></SelectTrigger>
              <SelectContent>{PAYMENTS.map(([v, l]) => <SelectItem key={v || "all"} value={v || "all"}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Operatore (email)</Label><Input value={operator} onChange={(e) => setOperator(e.target.value)} data-testid="rpt-op" /></div>
          <div><Label>Telefono cliente</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="rpt-phone" /></div>
          <div><Label>Codice ordine</Label><Input value={code} onChange={(e) => setCode(e.target.value)} data-testid="rpt-code" /></div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <div className="text-xs text-ink-500">
            Periodo: {summary?.period?.from?.slice(0, 10)} → {summary?.period?.to?.slice(0, 10)}
            {loading && " · caricamento…"}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={reset} className="rounded-full" data-testid="rpt-reset">Azzera filtri</Button>
            <Button onClick={load} className="rounded-full" data-testid="rpt-apply">Applica</Button>
          </div>
        </div>
      </section>

      {summary && (
        <>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Ordini" value={summary.orders_total} testid="kpi-orders" />
            <Kpi label="Attivi" value={summary.orders_active} testid="kpi-active" />
            <Kpi label="Consegnati" value={summary.orders_delivered} testid="kpi-delivered" />
            <Kpi label="Annullati" value={summary.orders_cancelled} testid="kpi-cancelled" />
            <Kpi label="Ricavi" value={eur(summary.revenue_cents)} testid="kpi-revenue" />
            <Kpi label="Ticket medio" value={eur(summary.avg_ticket_cents)} testid="kpi-avg" />
            <Kpi label="Clienti" value={summary.customers} testid="kpi-customers" />
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-6">
            <section className="card-elevated p-5">
              <div className="eyebrow mb-3">Serie per giorno</div>
              <table className="w-full text-sm">
                <thead className="text-ink-500 text-xs uppercase"><tr><th className="text-left py-1">Giorno</th><th className="text-right">Ordini</th><th className="text-right">Ricavi</th></tr></thead>
                <tbody>
                  {summary.series.map((d) => (
                    <tr key={d.day} className="border-t border-sand-300"><td className="py-1.5">{d.day}</td><td className="text-right">{d.orders}</td><td className="text-right">{eur(d.revenue_cents)}</td></tr>
                  ))}
                  {summary.series.length === 0 && <tr><td colSpan="3" className="py-3 text-ink-500">Nessun dato</td></tr>}
                </tbody>
              </table>
            </section>
            <section className="card-elevated p-5">
              <div className="eyebrow mb-3">Prodotti più venduti</div>
              <ul className="text-sm space-y-1">
                {summary.top_products.slice(0, 8).map((p) => <li key={p.name} className="flex justify-between"><span>{p.name}</span><span>{p.quantity}</span></li>)}
                {summary.top_products.length === 0 && <li className="text-ink-500">Nessun ordine consegnato nel periodo</li>}
              </ul>
              <div className="eyebrow mt-5 mb-3">Postazioni più attive</div>
              <ul className="text-sm space-y-1">
                {summary.top_stations.slice(0, 8).map((p) => <li key={p.name} className="flex justify-between"><span>{p.name}</span><span>{p.count}</span></li>)}
              </ul>
            </section>
          </div>
        </>
      )}

      <section className="card-elevated p-5 mt-6">
        <div className="eyebrow mb-3">Esportazioni (ultimi 30 giorni)</div>
        <div className="flex flex-wrap gap-2">
          <a href={`${BACKEND}/api/admin/exports/orders.csv?days=30`} target="_blank" rel="noreferrer" className="btn-outline" data-testid="export-csv">CSV</a>
          <a href={`${BACKEND}/api/admin/exports/orders.xlsx?days=30`} target="_blank" rel="noreferrer" className="btn-outline" data-testid="export-xlsx">Excel</a>
          <a href={`${BACKEND}/api/admin/exports/orders.pdf?days=30`} target="_blank" rel="noreferrer" className="btn-outline" data-testid="export-pdf">PDF</a>
        </div>
        <p className="text-xs text-ink-500 mt-3">Le esportazioni fisse ai 30gg tornano ai filtri classici. Le esportazioni filtrate sono nel backlog di Fase 4.</p>
      </section>
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="card-elevated p-4" data-testid={testid}>
      <div className="eyebrow">{label}</div>
      <div className="font-serif text-3xl mt-1">{value}</div>
    </div>
  );
}
